import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { BottomNav } from './BottomNav';
import { useEffect, useRef, useState } from 'react';
import { useBasiqSync } from '@/hooks/use-basiq-sync';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard, ArrowLeftRight, Landmark, PiggyBank, Receipt,
  Target, Trophy, FileText, Heart, Car, Package, BarChart3,
  Settings, Gift, ExternalLink, LogOut, Bell, ShieldCheck,
} from 'lucide-react';
import { useIsAdmin } from '@/hooks/use-admin';
import aussiLogo from '@/assets/aussibooks-logo.jpg';
import { useMemo } from 'react';
import { generateSmartNotifications } from '@/lib/smart-notifications';
import { Badge } from '@/components/ui/badge';

const AUTO_SYNC_COOLDOWN_MS = 5 * 60 * 1000;

const mainNav = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/transactions', icon: ArrowLeftRight, label: 'Transactions' },
  { to: '/accounts', icon: Landmark, label: 'Accounts' },
  { to: '/budget', icon: PiggyBank, label: 'Budget' },
];

const moreNav = [
  { to: '/bills', icon: Receipt, label: 'Bills' },
  { to: '/more/bank-sync', icon: Landmark, label: 'Bank Sync' },
  { to: '/more/savings', icon: Target, label: 'Savings Goals' },
  { to: '/more/challenges', icon: Trophy, label: 'Challenges' },
  { to: '/more/tax-wizard', icon: FileText, label: 'Tax Wizard' },
  { to: '/more/medicare', icon: Heart, label: 'Medicare' },
  { to: '/more/vehicles', icon: Car, label: 'Vehicles' },
  { to: '/more/assets', icon: Package, label: 'Assets' },
  { to: '/more/reports', icon: BarChart3, label: 'Reports' },
  { to: '/more/referral', icon: Gift, label: 'Referrals' },
  { to: '/more/inception-glen', icon: ExternalLink, label: 'Inception Glen' },
];

const bottomNav = [
  { to: '/more/settings', icon: Settings, label: 'Settings' },
];

function SidebarLink({ to, icon: Icon, label }: { to: string; icon: React.ElementType; label: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) => cn(
        'flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
        isActive
          ? 'bg-sidebar-accent text-sidebar-primary'
          : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {label}
    </NavLink>
  );
}

export function AppShell() {
  const { basiqUserId, syncTransactions, loading } = useBasiqSync();
  const { lock, data } = useAuth();
  const { isAdmin } = useIsAdmin();
  const lastSyncAttemptRef = useRef(0);
  const alertCount = useMemo(() => generateSmartNotifications(data).length, [data]);

  // Auto-sync on app resume (visibility change)
  useEffect(() => {
    if (!basiqUserId) return;

    const handler = () => {
      if (document.hidden || loading) return;
      const now = Date.now();
      if (now - lastSyncAttemptRef.current < AUTO_SYNC_COOLDOWN_MS) return;
      lastSyncAttemptRef.current = now;
      syncTransactions();
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [basiqUserId, syncTransactions, loading]);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-60 flex-col bg-sidebar border-r border-sidebar-border">
        {/* Branding */}
        <div className="p-4 border-b border-sidebar-border">
          <div className="flex items-center gap-2.5">
            <img src={aussiLogo} alt="AussiBooks" className="h-8 w-8 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <h1 className="text-sm font-bold text-sidebar-primary-foreground leading-tight">
                Inception Glen
              </h1>
              <p className="text-base font-bold text-sidebar-primary leading-none">AussiBooks</p>
            </div>
            <NavLink to="/" className="relative shrink-0 p-1.5 rounded-md hover:bg-sidebar-accent transition-colors">
              <Bell className="h-5 w-5 text-sidebar-foreground" />
              {alertCount > 0 && (
                <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] leading-none flex items-center justify-center">
                  {alertCount > 9 ? '9+' : alertCount}
                </Badge>
              )}
            </NavLink>
          </div>
        </div>

        {/* Main nav */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {mainNav.map(item => (
            <SidebarLink key={item.to} {...item} />
          ))}


          <div className="h-px mx-3 my-3 bg-sidebar-border" />
          {moreNav.map(item => (
            <SidebarLink key={item.to} {...item} />
          ))}
        </nav>

        {/* Bottom: Settings + Admin + Lock */}
        <div className="p-2 border-t border-sidebar-border space-y-0.5">
          {isAdmin && <SidebarLink to="/admin" icon={ShieldCheck} label="Admin" />}
          {bottomNav.map(item => (
            <SidebarLink key={item.to} {...item} />
          ))}
          <button
            onClick={lock}
            className="flex w-full items-center gap-2.5 px-3 py-1.5 rounded-md text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            Lock App
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom nav (hidden on desktop) */}
      <div className="md:hidden">
        <BottomNav />
      </div>
    </div>
  );
}
